import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';
import { collection, query, getDocs, doc, addDoc, updateDoc, serverTimestamp, where, limit as firestoreLimit } from 'firebase/firestore';
import { db, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { ArrowLeft, AlertCircle, CheckCircle, XCircle, UserPlus } from 'lucide-react';

interface ProcessResult {
  quoteId: string;
  email: string;
  success: boolean;
  offerId?: string;
  error?: string;
}

export default function BulkCreateOffers() {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const { toast } = useToast();

  const [processing, setProcessing] = useState(false);
  const [creatingUsers, setCreatingUsers] = useState(false);
  const [results, setResults] = useState<ProcessResult[]>([]);
  const [stats, setStats] = useState({ total: 0, succeeded: 0, failed: 0 });
  const [userCreationResults, setUserCreationResults] = useState<any>(null);

  // Redirect if not admin
  React.useEffect(() => {
    if (role && role !== 'admin') {
      navigate('/dashboard');
      toast({
        title: 'Access Denied',
        description: 'You need admin privileges to access this page.',
        variant: 'destructive',
      });
    }
  }, [role, navigate, toast]);

  const handleCreateUsers = async () => {
    if (!user || role !== 'admin') return;

    setCreatingUsers(true);
    setUserCreationResults(null);

    try {
      const createUsersFromQuotesCallable = httpsCallable(functions, 'createUsersFromQuotesCallable');
      const result = await createUsersFromQuotesCallable({});
      const data = result.data as any;

      if (data.success) {
        setUserCreationResults(data);
        toast({
          title: 'Users Created',
          description: `Created ${data.stats.created} new user accounts. ${data.stats.alreadyExisted} already existed.`,
        });
      } else {
        throw new Error(data.error || 'Failed to create users');
      }
    } catch (error: any) {
      console.error('Create users error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create user accounts.',
        variant: 'destructive',
      });
    } finally {
      setCreatingUsers(false);
    }
  };

  const lookupUserIdByEmail = async (email: string): Promise<string | null> => {
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', email),
        firestoreLimit(1)
      );
      const usersSnapshot = await getDocs(usersQuery);

      if (!usersSnapshot.empty) {
        return usersSnapshot.docs[0].id;
      }
      return null;
    } catch (error) {
      console.error(`Error looking up user for ${email}:`, error);
      return null;
    }
  };

  const createOfferFromQuote = async (quoteId: string, quoteData: any): Promise<ProcessResult> => {
    const email = quoteData.guardianEmail || quoteData.guardianInfo?.email;

    try {
      if (!email) {
        return { quoteId, email: 'unknown', success: false, error: 'No email found' };
      }

      const userId = await lookupUserIdByEmail(email);
      if (!userId) {
        return { quoteId, email, success: false, error: 'User account not found - must sign up first' };
      }

      // Determine locations
      const hubstopName = quoteData.hubstopName || quoteData.selectedHubstop;
      const pickupLocation = hubstopName || quoteData.pickupAddress;
      const dropoffLocation = quoteData.dropoffAddress;

      // Generate route name
      let routeName: string;
      if (quoteData.routeName) {
        routeName = quoteData.routeName;
      } else if (hubstopName) {
        routeName = `${hubstopName} - Summer 2026`;
      } else {
        const pickupArea = quoteData.pickupAddress?.split(',')[0] || quoteData.pickupZipcode;
        const dropoffArea = quoteData.dropoffAddress?.split(',')[0] || 'Destination';
        routeName = `${pickupArea} to ${dropoffArea} - Summer 2026`;
      }

      // Calculate pricing
      const studentCount = quoteData.students?.length || 1;
      const weeklyRate = quoteData.pricing?.perWeekPerStudent || 165;
      const availableWeeks = quoteData.selectedWeeks ||
        quoteData.selectedSessions?.map((s: string) => parseInt(s.replace('week', ''))) || [];

      // Set expiration
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 14);

      // Create offer
      const offerData = {
        routeName,
        routeCategory: 'summer-camp',
        pickupLocation,
        pickupTime: '',
        pickupInstructions: '',
        dropoffLocation,
        dropoffTime: '',
        dropoffInstructions: '',
        maxStudents: 6,
        currentEnrollment: 0,
        availableWeeks,
        allowPartialWeeks: true,
        baseWeeklyRate: weeklyRate,
        percentageAdjustment: 0,
        adjustedWeeklyRate: weeklyRate,
        adminNotes: `Auto-created from quote request #${quoteId}\nOriginal request date: ${quoteData.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}`,
        customerMessage: '',
        expiresInDays: 14,
        expiresAt,
        status: 'draft',
        recipients: [{
          userId,
          email,
          name: quoteData.guardianName || quoteData.guardianInfo?.firstName || email.split('@')[0],
          studentCount,
          studentIds: [],
          tier: 1,
          weeklyRate,
          subtotal: 0,
          totalAmount: 0,
          selectedWeeks: [],
          status: 'pending',
          quoteId,
        }],
        totalRecipientsCount: 1,
        activeRecipientsCount: 1,
        recipientUserIds: [userId],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user?.uid || 'bulk-create',
        sentAt: null,
      };

      const offerRef = await addDoc(collection(db, 'routeOffers'), offerData);

      // Update quote
      await updateDoc(doc(db, 'quoteRequests', quoteId), {
        offerId: offerRef.id,
        status: 'quoted',
        quotedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return { quoteId, email, success: true, offerId: offerRef.id };
    } catch (error: any) {
      return { quoteId, email, success: false, error: error.message };
    }
  };

  const handleBulkCreate = async () => {
    if (!user || role !== 'admin') return;

    setProcessing(true);
    setResults([]);
    setStats({ total: 0, succeeded: 0, failed: 0 });

    try {
      // Get all quotes without offers
      const quotesSnapshot = await getDocs(collection(db, 'quoteRequests'));
      const quotesNeedingOffers = quotesSnapshot.docs.filter(doc => !doc.data().offerId);

      const total = quotesNeedingOffers.length;
      setStats(prev => ({ ...prev, total }));

      if (total === 0) {
        toast({
          title: 'No Quotes to Process',
          description: 'All quotes already have offers.',
        });
        setProcessing(false);
        return;
      }

      const processedResults: ProcessResult[] = [];

      for (const quoteDoc of quotesNeedingOffers) {
        const result = await createOfferFromQuote(quoteDoc.id, quoteDoc.data());
        processedResults.push(result);

        setResults([...processedResults]);
        setStats({
          total,
          succeeded: processedResults.filter(r => r.success).length,
          failed: processedResults.filter(r => !r.success).length,
        });
      }

      toast({
        title: 'Bulk Creation Complete',
        description: `Created ${processedResults.filter(r => r.success).length} offers from ${total} quotes.`,
      });
    } catch (error: any) {
      console.error('Bulk create error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to process quotes.',
        variant: 'destructive',
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Button
        variant="outline"
        onClick={() => navigate('/admin/quote-requests')}
        className="mb-6 flex items-center gap-2"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Quote Requests
      </Button>

      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Bulk Create Route Offers</CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              This will automatically create draft route offers for all quote requests that don't have offers yet.
              Offers will be created as drafts so you can review and send them manually.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Two-Step Process:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li><strong>Step 1:</strong> Create user accounts for all quote request emails (if not already created)</li>
                    <li><strong>Step 2:</strong> Create draft route offers for all quotes with user accounts</li>
                  </ol>
                  <p className="mt-2">Users will be prompted to set a password when they first access their route offer.</p>
                </div>
              </div>

              {/* Step 1: Create User Accounts */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <UserPlus className="w-5 h-5" />
                  Step 1: Create User Accounts
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Create Firebase accounts for all quote requests that don't have accounts yet.
                </p>
                <Button
                  onClick={handleCreateUsers}
                  disabled={creatingUsers || processing}
                  loading={creatingUsers}
                  size="lg"
                  className="w-full"
                  variant="outline"
                >
                  {creatingUsers ? 'Creating User Accounts...' : 'Create User Accounts from Quotes'}
                </Button>

                {/* User Creation Results */}
                {userCreationResults && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 font-medium mb-2">✓ User accounts processed!</p>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="font-semibold text-green-900">{userCreationResults.stats.created}</div>
                        <div className="text-green-700">Created</div>
                      </div>
                      <div>
                        <div className="font-semibold text-blue-900">{userCreationResults.stats.alreadyExisted}</div>
                        <div className="text-blue-700">Already Existed</div>
                      </div>
                      <div>
                        <div className="font-semibold text-red-900">{userCreationResults.stats.failed}</div>
                        <div className="text-red-700">Failed</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Step 2: Create Offers */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-3">Step 2: Create Route Offers</h3>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3 mb-4">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">Important Notes:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Only quotes from users with accounts will be processed</li>
                      <li>Offers will be created as <strong>drafts</strong> (not sent automatically)</li>
                      <li>You can review and send each offer individually from the Route Offers page</li>
                      <li>Quotes that fail will be shown below with the reason</li>
                    </ul>
                  </div>
                </div>

                <Button
                  onClick={handleBulkCreate}
                  disabled={processing || creatingUsers}
                  loading={processing}
                  size="lg"
                  className="w-full"
                >
                  {processing ? 'Processing Quotes...' : 'Create Offers from All Quotes'}
                </Button>
              </div>

              {/* Stats */}
              {stats.total > 0 && (
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
                        <div className="text-sm text-gray-600">Total Quotes</div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{stats.succeeded}</div>
                        <div className="text-sm text-gray-600">Succeeded</div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
                        <div className="text-sm text-gray-600">Failed</div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Results */}
              {results.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Processing Results</h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {results.map((result, index) => (
                      <div
                        key={index}
                        className={`flex items-start gap-3 p-3 rounded-lg border ${
                          result.success
                            ? 'bg-green-50 border-green-200'
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        {result.success ? (
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 text-sm">
                          <div className="font-medium">{result.email}</div>
                          {result.success ? (
                            <div className="text-green-700">
                              Created offer: {result.offerId}
                            </div>
                          ) : (
                            <div className="text-red-700">{result.error}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
